import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  TextField,
  CircularProgress,
  Chip,
  Tab,
  Tabs,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload,
  CheckCircle,
  Clear,
  Description,
  Download,
} from '@mui/icons-material';
import {
  FaBarcode,
  FaQrcode,
  FaFileExcel,
  FaDownload,
} from 'react-icons/fa';
import { useActivityLog } from '../context/ActivityLogContext';
import API_BASE_URL, { apiCall } from '../config/api';
import { formatLogTime } from '../utils/dateUtils';
import InterfaceHeader from '../components/InterfaceHeader';
import { TOOL_COLORS } from '../constants/colors';

const BARCODE_TYPES = [
  { value: 'code128', label: 'Code 128', description: 'Alphanumeric, variable length. Most versatile linear barcode.' },
  { value: 'qr', label: 'QR Code', description: '2D matrix barcode. Supports URLs, text, up to ~4000 chars.' },
  { value: 'upca', label: 'UPC-A', description: '12-digit numeric only. Standard US retail product barcode.' },
  { value: 'ean13', label: 'EAN-13', description: '13-digit numeric only. International retail product barcode.' },
  { value: 'code39', label: 'Code 39', description: 'Alphanumeric + symbols. Common in non-retail (logistics, military).' },
  { value: 'itf14', label: 'ITF-14', description: '14-digit numeric. Used for carton/pallet identification.' },
];

const BarcodeGenerator = () => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const logEndRef = useRef(null);
  const isDark = theme.palette.mode === 'dark';

  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setContextLogs,
    addLog: contextAddLog,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Single generation state
  const [singleText, setSingleText] = useState('');
  const [singleType, setSingleType] = useState('code128');
  const [singleWidth, setSingleWidth] = useState(300);
  const [singleHeight, setSingleHeight] = useState(150);
  const [singleGenerating, setSingleGenerating] = useState(false);
  const [singlePreview, setSinglePreview] = useState(null);
  const [singleBlob, setSingleBlob] = useState(null);

  // Batch generation state
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [logs, setLogs] = useState([]);

  const toolColor = TOOL_COLORS.OTHER;

  const addLog = (processId, message) => {
    const formattedMessage = `[${formatLogTime()}] ${message}`;
    contextAddLog(processId, formattedMessage);
  };

  // Auto-scroll logs
  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        if (processIdRef.current) {
          clearIntervalRef(processIdRef.current);
        }
      }
    };
  }, [clearIntervalRef]);

  // ========== Single Generation ==========
  const handleGenerateSingle = async () => {
    if (!singleText.trim()) return;
    setSingleGenerating(true);
    setSinglePreview(null);
    setSingleBlob(null);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/BarcodeGenerator/generate-single`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`,
        },
        body: JSON.stringify({
          text: singleText.trim(),
          type: singleType,
          width: singleWidth,
          height: singleHeight,
        }),
      });

      if (!response.ok) {
        let errorMsg = 'Generation failed';
        try {
          const errData = await response.json();
          errorMsg = errData.message || errorMsg;
        } catch { /* ignore parse error */ }
        throw new Error(errorMsg);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setSinglePreview(url);
      setSingleBlob(blob);
    } catch (err) {
      setError(err.message || 'Failed to generate barcode');
    } finally {
      setSingleGenerating(false);
    }
  };

  const handleDownloadSingle = () => {
    if (!singleBlob) return;
    const url = URL.createObjectURL(singleBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${singleText.trim().replace(/[^a-zA-Z0-9_-]/g, '_')}_${singleType}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ========== Batch Generation ==========
  const fetchStatus = async (processId) => {
    try {
      const response = await apiCall(`${API_BASE_URL}/api/BarcodeGenerator/status/${processId}`);

      if (response.logs && response.logs.length > 0) {
        setLogs(response.logs);
        setContextLogs(processId, response.logs);
      }

      if (response.progressPercentage !== undefined) {
        setProgress(response.progressPercentage);
        updateActivityProcess(processId, { progress: response.progressPercentage });
      }

      if (response.status === 'completed') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(processId);
        }

        if (!downloadTriggeredRef.current) {
          downloadTriggeredRef.current = true;
          await downloadFile(processId, response.fileName, response.awsUrl);
        }

        setResults({
          success: true,
          fileCount: response.processedItems || 0,
          failedCount: response.failedItems || 0,
          fileName: response.fileName,
        });
        completeActivityProcess(processId, 'success', 'Barcode batch generation completed');
        setSuccess('Processing complete! ZIP file with barcode images has been generated.');
        setLoading(false);
        setProgress(null);
      } else if (response.status === 'error') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(processId);
        }
        setError(`Error: ${response.message}`);
        setLoading(false);
        setProgress(null);
        completeActivityProcess(processId, 'error', response.message);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const downloadFile = async (processId, serverFileName, awsUrl) => {
    try {
      addLog(processId, 'Downloading file...');

      if (awsUrl) {
        const link = document.createElement('a');
        link.href = awsUrl;
        link.download = serverFileName || 'Barcode Generator Result.zip';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.open(awsUrl, '_blank');
        addLog(processId, 'Download complete!');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/BarcodeGenerator/download/${processId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = serverFileName || 'Barcode Generator Result.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
      addLog(processId, 'Download complete!');
    } catch (err) {
      console.error('Error downloading file:', err);
      addLog(processId, `Download error: ${err.message}`);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/BarcodeGenerator/sample`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) throw new Error('Failed to download sample file');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      link.download = `Barcode Generator Template ${now.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download sample file.');
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const droppedFile = droppedFiles[0];
    if (droppedFile && droppedFile.name.toLowerCase().match(/\.(xlsx|xls)$/)) {
      setFile(droppedFile);
      setError('');
    } else {
      setError('Please drop an Excel file only (.xlsx, .xls)');
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().match(/\.(xlsx|xls)$/)) {
        setError('Please select an Excel file only.');
        return;
      }
      setFile(selectedFile);
      setError('');
      setSuccess('');
    }
  };

  const handleProcessBatch = async () => {
    if (!file) {
      setError('Please select a file first.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(0);
    setLogs([]);
    downloadTriggeredRef.current = false;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiCall(`${API_BASE_URL}/api/BarcodeGenerator/generate-batch`, {
        method: 'POST',
        body: formData,
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        processIdRef.current = serverProcessId;

        addActivityProcess(serverProcessId, 'Processing Barcode Generator', 'Barcode Generator');
        addLog(serverProcessId, 'Starting batch barcode generation...');

        intervalRef.current = setInterval(() => fetchStatus(serverProcessId), 1000);
        setIntervalRef(serverProcessId, intervalRef.current);
      }
    } catch (err) {
      console.error('Processing error:', err);
      setError(err.message || 'An error occurred during processing. Please try again.');
      setLoading(false);
      setProgress(null);
    }
  };

  const resetForm = () => {
    setFile(null);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(null);
    setLogs([]);
    setSinglePreview(null);
    setSingleBlob(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <InterfaceHeader
          title="BARCODE / QR GENERATOR"
          color={TOOL_COLORS.OTHER.primary}
          category="others"
        />

        <Grid container spacing={3}>
          {/* Main Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Tabs
                value={activeTab}
                onChange={(e, newVal) => { setActiveTab(newVal); setError(''); }}
                sx={{
                  mb: 3,
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
                  '& .Mui-selected': { color: toolColor.primary },
                  '& .MuiTabs-indicator': { backgroundColor: toolColor.primary },
                }}
              >
                <Tab label="Single" icon={<FaBarcode style={{ marginRight: 6 }} />} iconPosition="start" />
                <Tab label="Batch" icon={<FaFileExcel style={{ marginRight: 6 }} />} iconPosition="start" />
              </Tabs>

              {/* ===== Single Tab ===== */}
              {activeTab === 0 && (
                <Box>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                    Enter text and select a barcode type to generate a single barcode or QR code image.
                  </Typography>

                  <TextField
                    fullWidth
                    label="Text / Value"
                    placeholder="Enter text, item ID, URL, or numeric code"
                    value={singleText}
                    onChange={(e) => { setSingleText(e.target.value); setSinglePreview(null); setSingleBlob(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && singleText.trim()) handleGenerateSingle(); }}
                    disabled={singleGenerating}
                    sx={{ mb: 2 }}
                  />

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Barcode Type</InputLabel>
                    <Select
                      value={singleType}
                      onChange={(e) => { setSingleType(e.target.value); setSinglePreview(null); setSingleBlob(null); }}
                      label="Barcode Type"
                    >
                      {BARCODE_TYPES.map((bt) => (
                        <MenuItem key={bt.value} value={bt.value}>
                          {bt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Width (px)"
                        type="number"
                        value={singleWidth}
                        onChange={(e) => setSingleWidth(Math.max(50, Math.min(2000, parseInt(e.target.value) || 300)))}
                        inputProps={{ min: 50, max: 2000 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Height (px)"
                        type="number"
                        value={singleHeight}
                        onChange={(e) => setSingleHeight(Math.max(50, Math.min(2000, parseInt(e.target.value) || 150)))}
                        inputProps={{ min: 50, max: 2000 }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleGenerateSingle}
                      disabled={!singleText.trim() || singleGenerating}
                      sx={{
                        bgcolor: toolColor.primary,
                        '&:hover': { bgcolor: toolColor.hover },
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                      }}
                      startIcon={singleGenerating ? <CircularProgress size={18} color="inherit" /> : <FaBarcode />}
                    >
                      {singleGenerating ? 'Generating...' : 'Generate'}
                    </Button>

                    {singleBlob && (
                      <Button
                        variant="outlined"
                        onClick={handleDownloadSingle}
                        sx={{
                          borderColor: toolColor.primary,
                          color: toolColor.primary,
                          '&:hover': { borderColor: toolColor.hover, bgcolor: toolColor.light },
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                        startIcon={<FaDownload />}
                      >
                        Download PNG
                      </Button>
                    )}
                  </Box>

                  {/* Preview */}
                  {singlePreview && (
                    <Card sx={{ mt: 3, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fafafa', border: `1px solid ${theme.palette.divider}` }}>
                      <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.secondary }}>
                          Preview
                        </Typography>
                        <Box
                          sx={{
                            display: 'inline-block',
                            p: 2,
                            bgcolor: '#ffffff',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                          }}
                        >
                          <img
                            src={singlePreview}
                            alt="Generated barcode"
                            style={{ maxWidth: '100%', maxHeight: 300, display: 'block' }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: theme.palette.text.disabled }}>
                          {singleText} ({BARCODE_TYPES.find(b => b.value === singleType)?.label || singleType})
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}

              {/* ===== Batch Tab ===== */}
              {activeTab === 1 && (
                <Box>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                    Upload an Excel file with "Text" and "Type" columns. Each row generates a barcode image. Results are returned as a ZIP file.
                  </Typography>

                  {/* File Upload Area */}
                  <Card sx={{ mb: 3, bgcolor: file ? (isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Description sx={{ color: toolColor.primary }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          Excel File
                        </Typography>
                        {file && (
                          <Tooltip title="1 file selected">
                            <CheckCircle sx={{ color: '#4CAF50' }} />
                          </Tooltip>
                        )}
                      </Box>

                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                      />

                      <Box
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                          border: `2px dashed ${isDragging ? toolColor.primary : file ? '#4CAF50' : theme.palette.divider}`,
                          borderRadius: 2,
                          p: 3,
                          textAlign: 'center',
                          bgcolor: isDragging
                            ? toolColor.light
                            : file
                              ? (isDark ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                              : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          mb: 2,
                          '&:hover': {
                            borderColor: file ? '#4CAF50' : toolColor.primary,
                            bgcolor: file
                              ? (isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                              : toolColor.light,
                          }
                        }}
                      >
                        {!file ? (
                          <>
                            <CloudUpload sx={{ fontSize: 48, color: isDragging ? toolColor.primary : 'text.secondary', mb: 1 }} />
                            <Typography variant="body1" gutterBottom>
                              {isDragging ? 'Drop file here' : 'Drop your Excel file here or click to browse'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Excel (.xlsx, .xls) files with Text and Type columns
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                            <Typography variant="body1" gutterBottom>
                              1 file selected
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Click or drop to replace
                            </Typography>
                          </>
                        )}
                      </Box>

                      {file && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                              borderColor: toolColor.primary,
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            <Description sx={{ color: toolColor.primary, fontSize: 20 }} />
                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                              ({formatFileSize(file.size)})
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <Clear fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handleProcessBatch}
                      disabled={!file || loading}
                      sx={{
                        bgcolor: toolColor.primary,
                        '&:hover': { bgcolor: toolColor.hover },
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                      }}
                      startIcon={<FaBarcode />}
                    >
                      {loading ? 'Processing...' : 'Process'}
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={handleDownloadSample}
                      disabled={loading}
                      sx={{
                        borderColor: toolColor.primary,
                        color: toolColor.primary,
                        '&:hover': { borderColor: toolColor.hover, bgcolor: toolColor.light },
                        textTransform: 'none',
                      }}
                      startIcon={<FaDownload />}
                    >
                      Download Template
                    </Button>

                    {(file || results) && (
                      <Button
                        variant="outlined"
                        onClick={resetForm}
                        disabled={loading}
                        sx={{
                          borderColor: theme.palette.divider,
                          color: theme.palette.text.primary,
                          '&:hover': {
                            borderColor: theme.palette.text.secondary,
                            bgcolor: alpha(theme.palette.text.primary, 0.05),
                          },
                          textTransform: 'none',
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>

                  {/* Progress */}
                  {loading && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Generating barcodes...
                      </Typography>
                      <LinearProgress
                        variant={progress > 0 ? 'determinate' : 'indeterminate'}
                        value={progress}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: isDark ? 'rgba(255, 140, 0, 0.2)' : 'rgba(255, 140, 0, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: toolColor.primary,
                            borderRadius: 4,
                          },
                        }}
                      />
                      {progress > 0 && (
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
                          {Math.round(progress)}% complete
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Right Sidebar */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Downloads
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <FaBarcode color={toolColor.primary} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${results.fileCount || 0} Barcodes Generated`}
                        secondary={results.failedCount > 0 ? `${results.failedCount} failed` : 'All successful'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FaFileExcel color="#4CAF50" />
                      </ListItemIcon>
                      <ListItemText
                        primary="ZIP File Generated"
                        secondary="Contains PNG images + index.xlsx"
                      />
                    </ListItem>
                  </List>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    ZIP file has been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}>
                <strong>Single Mode:</strong><br />
                1. Enter text or an item ID<br />
                2. Select the barcode type<br />
                3. Adjust width/height if needed<br />
                4. Click "Generate" to preview<br />
                5. Click "Download PNG" to save<br />
                <br />
                <strong>Batch Mode:</strong><br />
                1. Download the template Excel file<br />
                2. Fill in Text and Type columns<br />
                3. Upload the file<br />
                4. Click "Process" to generate all<br />
                5. ZIP auto-downloads with all images
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Supported Types:
              </Typography>
              <List dense>
                {BARCODE_TYPES.map((bt) => (
                  <ListItem key={bt.value} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {bt.value === 'qr' ? (
                        <FaQrcode size={14} color={toolColor.primary} />
                      ) : (
                        <FaBarcode size={14} color={toolColor.primary} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {bt.label}
                          <Chip label={bt.value} size="small" sx={{ ml: 1, fontSize: 10, height: 18 }} />
                        </Typography>
                      }
                      secondary={bt.description}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BarcodeGenerator;
